import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Upload, X, File } from 'lucide-react';

interface CustomFileUploadProps {
  onChange: (file: File | null) => void;
  error?: string;
  placeholder?: string;
  accept?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const CustomFileUpload: React.FC<CustomFileUploadProps> = ({
  onChange,
  error,
  placeholder = "Choose a file",
  accept,
  disabled = false,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    onChange(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept={accept}
        disabled={disabled}
        className="hidden"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
      
      {!selectedFile ? (
        <Button
          type="button"
          variant="outline"
          onClick={handleButtonClick}
          disabled={disabled}
          className={`w-full justify-start text-left font-normal ${
            error ? 'border-destructive' : ''
          } text-muted-foreground hover:text-foreground ${disabled ? 'bg-muted/50' : ''}`}
        >
          <Upload className="mr-2 h-4 w-4" />
          {placeholder}
        </Button>
      ) : (
        <div className={`flex items-center justify-between w-full px-3 py-2 border rounded-md bg-background ${
          error ? 'border-destructive' : 'border-input'
        } ${disabled ? 'bg-muted/50 opacity-60' : ''}`}>
          <div className="flex items-center flex-1 min-w-0">
            <File className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleButtonClick}
              disabled={disabled}
              className="h-8 px-2"
            >
              <Upload className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveFile}
              disabled={disabled}
              className="h-8 px-2 text-destructive hover:text-destructive-foreground hover:bg-destructive"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
      
      {error && (
        <p id={ariaDescribedBy} className="text-destructive text-sm mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default CustomFileUpload;